import { useId, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { commitTagText, removeTagAt, tagCountLine } from '../../lib/portalAssets/tagChips';

// THE TAG FIELD (Run C): chips you type into. Enter or a comma commits one, × removes one, Backspace in the empty box removes
// the last, and a pasted comma-separated string splits into chips (it is what the old text field took, and people paste).
// ONE component, used by the staff dialog and the club's detail page (both render AssetDetailView).
//
// ⚠️ Every chip comes out of `normalizeAssetTags` via tagChips.ts — it previews the STORED value, it does not re-implement
// it. ⚠️ The limits are enforced where they are SEEN: a refused tag stays in the box with the reason beside it, and the count
// is shown before the limit is reached. ⚠️ NO auto-save: the host keeps its explicit Save button (matching Save title and
// Save categories). ⚠️ A tag typed but not yet committed is committed when the box loses focus, so clicking Save never
// silently leaves it behind.

export function TagChipInput({
  value,
  onChange,
  disabled = false,
  id,
}: {
  value: readonly string[];
  onChange: (tags: string[]) => void;
  disabled?: boolean;
  id?: string;
}) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  /** `after` is text typed past the last comma: it stays in the box as the next tag in progress. */
  function commit(text: string, after = ''): boolean {
    const r = commitTagText(value, text);
    if (r.tags.length !== value.length || r.tags.some((t, i) => t !== value[i])) onChange(r.tags);
    setDraft(r.rest !== '' && after !== '' ? `${r.rest},${after}` : r.rest + after);
    setError(r.error);
    return r.error === null;
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      // ⚠️ Enter commits a chip; it must not also submit the host's form (that is what the Save button is for).
      e.preventDefault();
      commit(draft);
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      e.preventDefault();
      setError(null);
      onChange(removeTagAt(value, value.length - 1));
    }
  }

  function onPaste(e: ClipboardEvent<HTMLInputElement>) {
    const text = e.clipboardData.getData('text');
    if (!text.includes(',') && !text.includes('\n')) return; // an ordinary single tag pastes into the box as typed text
    e.preventDefault();
    commit(draft + text.replace(/\r?\n/g, ','));
  }

  return (
    <div className="space-y-1">
      <div
        className="flex min-h-10 flex-wrap items-center gap-1.5 rounded-md border border-input bg-background px-2 py-1.5 focus-within:ring-2 focus-within:ring-ring"
        data-testid="tag-chip-input"
      >
        {value.map((t, i) => (
          <span key={t} className="inline-flex items-center gap-1 rounded bg-muted px-2 py-0.5 text-xs text-foreground" data-testid="tag-chip">
            {t}
            <button
              type="button"
              aria-label={`Remove tag ${t}`}
              disabled={disabled}
              className="rounded text-muted-foreground hover:text-foreground"
              onClick={() => {
                setError(null);
                onChange(removeTagAt(value, i));
              }}
            >
              ×
            </button>
          </span>
        ))}
        <input
          id={inputId}
          className="min-w-[8rem] flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground"
          value={draft}
          disabled={disabled}
          placeholder={value.length === 0 ? 'e.g. summer, group class' : ''}
          onChange={(e) => {
            const v = e.target.value;
            // ⚠️ A comma can arrive without a keydown whose key is ',' (mobile and IME keyboards report 'Unidentified'), so the
            // value is checked here too: everything before the last comma becomes chips, the rest stays as the next tag.
            const comma = v.lastIndexOf(',');
            if (comma >= 0) {
              commit(v.slice(0, comma), v.slice(comma + 1));
              return;
            }
            setDraft(v);
            if (error) setError(null);
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          onBlur={() => {
            if (draft.trim() !== '') commit(draft);
          }}
        />
      </div>
      <p className="text-xs text-muted-foreground">Type a tag and press Enter or comma. {tagCountLine(value.length)}.</p>
      {error && (
        <p className="text-xs text-foreground" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
